import { appendBundleGiftItems } from 'gift-config';

/**
 * Back to School 2026 — campaign BUY NOW (data-id variant, comma-separated multi-add)
 */
class Bts26CartManager {
  constructor() {
    this.isSubmitting = false;
    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener("click", (event) => {
      const buyButton = event.target.closest(".campaign-buy-btn");
      if (!buyButton) return;

      event.preventDefault();
      event.stopPropagation();

      if (buyButton.hasAttribute("data-processing")) return;
      this.handleBuyButtonClick(buyButton);
    });
  }

  async handleBuyButtonClick(button) {
    if (this.isSubmitting || button.hasAttribute("data-processing")) return;

    try {
      this.isSubmitting = true;
      button.setAttribute("data-processing", "true");
      this.setButtonLoading(button, true);

      const productIds = this.getProductIds(button);
      if (!productIds.length) throw new Error("未找到有效的商品ID");

      await this.addProductsToCart(productIds);
      window.location.href = "/cart";
    } catch (error) {
      console.error("添加商品到购物车失败:", error);
    } finally {
      this.isSubmitting = false;
      button.removeAttribute("data-processing");
      this.setButtonLoading(button, false);
    }
  }

  getProductIds(button) {
    const dataId = button.getAttribute("data-id");
    if (!dataId) return [];
    return dataId
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id);
  }

  async addProductsToCart(productIds) {
    // 主品 + gift-config bundleAdd 有货礼品
    const baseItems = productIds.map((id) => ({ id, quantity: 1 }));
    const items = await appendBundleGiftItems(baseItems);

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || "添加商品失败");
    }

    return response.json();
  }

  setButtonLoading(button, loading) {
    if (loading) {
      button.setAttribute("aria-busy", "true");
      button.style.pointerEvents = "none";
      button.style.opacity = "0.7";
    } else {
      button.removeAttribute("aria-busy");
      button.style.pointerEvents = "";
      button.style.opacity = "";
    }
  }
}

let bts26CartManager = null;

function initBts26CartManager() {
  if (!bts26CartManager) {
    bts26CartManager = new Bts26CartManager();
  }
}

/**
 * MINI Robot Picks carousel — native scroll-snap + progress + arrows
 */
class Bts26MiniCarousel {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector("[data-bts26-mini-track]");
    this.progress = root.querySelector("[data-bts26-mini-progress]");
    this.prevBtn = root.querySelector("[data-bts26-mini-prev]");
    this.nextBtn = root.querySelector("[data-bts26-mini-next]");
    if (!this.track) return;

    this.onScroll = this.onScroll.bind(this);
    this.onPrev = this.onPrev.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onResize = this.onResize.bind(this);

    this.track.addEventListener("scroll", this.onScroll, { passive: true });
    this.prevBtn?.addEventListener("click", this.onPrev);
    this.nextBtn?.addEventListener("click", this.onNext);
    window.addEventListener("resize", this.onResize);

    this.update();
  }

  getStep() {
    const slide = this.track.querySelector("[data-bts26-mini-slide]");
    if (!slide) return this.track.clientWidth;
    const styles = window.getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  onPrev() {
    this.track.scrollBy({ left: -this.getStep(), behavior: "smooth" });
  }

  onNext() {
    this.track.scrollBy({ left: this.getStep(), behavior: "smooth" });
  }

  onScroll() {
    this.update();
  }

  onResize() {
    this.update();
  }

  update() {
    const { scrollLeft, clientWidth, scrollWidth } = this.track;
    const maxScroll = scrollWidth - clientWidth;
    // Match Figma: fill = how far through content (visible end / total)
    const ratio = scrollWidth > 0 ? (scrollLeft + clientWidth) / scrollWidth : 1;
    const clamped = Math.min(1, Math.max(0, ratio));

    if (this.progress) {
      this.progress.style.setProperty("--bts26-mini-progress", String(clamped));
      this.progress.setAttribute("aria-valuenow", String(Math.round(clamped * 100)));
    }

    const atStart = scrollLeft <= 1;
    const atEnd = maxScroll <= 1 || scrollLeft >= maxScroll - 1;
    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;
  }
}

function initBts26MiniCarousels() {
  document.querySelectorAll("[data-bts26-mini-carousel]").forEach((root) => {
    if (root.dataset.bts26MiniReady === "1") return;
    root.dataset.bts26MiniReady = "1";
    new Bts26MiniCarousel(root);
  });
}

/** Same 15 parallelogram segments as bts26-product-card__tier-bar */
const BTS26_COMBO_TIER_SEGS = [
  "M26.5195 21.6504H6C4.89543 21.6504 4 20.755 4 19.6504V6.34766C4 5.24309 4.89543 4.34766 6 4.34766H31.4805L26.5195 21.6504Z",
  "M59.5195 21.6504H28.5195L33.4805 4.34863H64.4805L59.5195 21.6504Z",
  "M92.5195 21.6504H61.5195L66.4805 4.34863H97.4805L92.5195 21.6504Z",
  "M125.52 21.6504H94.5195L99.4805 4.34863H130.48L125.52 21.6504Z",
  "M158.52 21.6504H127.52L132.48 4.34863H163.48L158.52 21.6504Z",
  "M191.52 21.6504H160.52L165.48 4.34863H196.48L191.52 21.6504Z",
  "M224.52 21.6504H193.52L198.48 4.34863H229.48L224.52 21.6504Z",
  "M257.52 21.6504H226.52L231.48 4.34863H262.48L257.52 21.6504Z",
  "M290.52 21.6504H259.52L264.48 4.34863H295.48L290.52 21.6504Z",
  "M323.52 21.6504H292.52L297.48 4.34863H328.48L323.52 21.6504Z",
  "M356.52 21.6504H325.52L330.48 4.34863H361.48L356.52 21.6504Z",
  "M389.52 21.6504H358.52L363.48 4.34863H394.48L389.52 21.6504Z",
  "M422.52 21.6504H391.52L396.48 4.34863H427.48L422.52 21.6504Z",
  "M455.52 21.6504H424.52L429.48 4.34863H460.48L455.52 21.6504Z",
  "M483 4.34766C484.105 4.34766 485 5.24309 485 6.34766V19.6504C485 20.755 484.105 21.6504 483 21.6504H457.52L462.48 4.34766H483Z",
];

const BTS26_COMBO_TIER_GRAD_ENDS = [
  31.4805, 64.4805, 97.4805, 130.48, 163.48, 196.48, 229.48, 262.48, 295.48,
  328.48, 361.48, 394.48, 427.48, 460.48, 485,
];

class Bts26ComboTierSync {
  constructor(root) {
    this.root = root;
    this.bar = root.querySelector("[data-bts26-combo-tier]");
    this.path = root.querySelector("[data-bts26-combo-tier-path]");
    this.grad = root.querySelector("[data-bts26-combo-tier-grad]");
    this.progress = root.querySelector(".promotion-progress");
    this.totalEl = root.querySelector("[data-total]");
    this.segCount = -1;

    if (this.progress) {
      this.observer = new MutationObserver(() => this.syncFromProgress());
      this.observer.observe(this.progress, {
        attributes: true,
        attributeFilter: ["style"],
      });
      this.syncFromProgress();
    }

    if (this.totalEl) {
      this.totalObserver = new MutationObserver(() => this.formatTotal());
      this.totalObserver.observe(this.totalEl, {
        characterData: true,
        childList: true,
        subtree: true,
      });
      this.formatTotal();
    }
  }

  syncFromProgress() {
    if (!this.progress || !this.path || !this.bar) return;

    const width = this.progress.style.width || "0%";
    const pct = Math.min(100, Math.max(0, parseFloat(width) || 0));
    const segs = Math.round((pct / 100) * BTS26_COMBO_TIER_SEGS.length);

    if (segs === this.segCount) return;
    this.segCount = segs;

    const d = BTS26_COMBO_TIER_SEGS.slice(0, segs).join("");
    this.path.setAttribute("d", d);

    const fillW = segs > 0 ? BTS26_COMBO_TIER_GRAD_ENDS[segs - 1] : 9.5;
    if (this.grad) this.grad.setAttribute("x1", String(fillW));

    this.bar.setAttribute("aria-valuenow", String(Math.round(pct)));
  }

  formatTotal() {
    if (!this.totalEl || this._formattingTotal) return;
    const raw = (this.totalEl.textContent || "").trim();
    if (!raw) return;

    // "$2,078.00" / "$2,078" → "2,078"（$ 由独立 span 展示）
    const value = raw.replace(/^\$/, "").replace(/\.00$/, "");
    if (raw === value) return;

    this._formattingTotal = true;
    this.totalEl.textContent = value;
    this._formattingTotal = false;
  }
}

function initBts26ComboTierSync() {
  document.querySelectorAll(".bts26-combo").forEach((root) => {
    if (root.dataset.bts26ComboTierReady === "1") return;
    root.dataset.bts26ComboTierReady = "1";
    new Bts26ComboTierSync(root);
  });
}

function initBts26FansMarquee() {
  document
    .querySelectorAll(".bts26-fans .bts26-fans__reviews-track")
    .forEach((track) => {
      const first = track.querySelector(
        ".bts26-fans__reviews-set:not([aria-hidden='true'])",
      );
      if (!first) return;
      const gap = parseFloat(getComputedStyle(track).gap) || 0;
      track.style.setProperty(
        "--bts26-fans-half",
        `${first.offsetWidth + gap}px`,
      );
    });
}

/**
 * Fans Saying 视频顺序自动播放（对齐父亲节 FansSayingVideoManager）
 * 区块进入视口后按 DOM 顺序播放，结束/失败后切到下一个并循环。
 */
class Bts26FansVideoManager {
  constructor() {
    this.videos = [];
    this.currentVideoIndex = 0;
    this.isPlaying = false;
    this.hasStarted = false;
    this.observer = null;
    this.sectionElement = null;
    this.eventHandlers = new Map();
    this.retryCount = 0;
    this.maxRetries = 3;
    this.videoLoadTimeout = 10000;
    this.init();
  }

  init() {
    this.sectionElement = document.getElementById("bts26-fans");
    if (!this.sectionElement) return;

    this.findVideos();
    if (!this.videos.length) return;

    this.setupIntersectionObserver();
    this.bindVideoEvents();
  }

  findVideos() {
    this.videos = Array.from(this.sectionElement.querySelectorAll("video"));
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasStarted) {
            this.hasStarted = true;
            this.startVideoSequence();
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: "0px 0px -10% 0px",
      },
    );
    this.observer.observe(this.sectionElement);
  }

  bindVideoEvents() {
    this.videos.forEach((video, index) => {
      if (video.dataset.eventsBound === "true") return;

      video.muted = true;

      const onEnded = () => this.onVideoEnded(index);
      const onError = () => this.onVideoError(index);

      video.addEventListener("ended", onEnded);
      video.addEventListener("error", onError);
      this.eventHandlers.set(video, { onEnded, onError });
      video.dataset.eventsBound = "true";
    });
  }

  async waitForVideosToLoad() {
    const loadPromises = this.videos.map((video) => {
      return new Promise((resolve) => {
        if (video.readyState >= 2) {
          resolve();
          return;
        }

        const timeout = setTimeout(resolve, this.videoLoadTimeout);

        const cleanup = () => {
          clearTimeout(timeout);
          video.removeEventListener("loadeddata", onReady);
          video.removeEventListener("canplay", onReady);
          video.removeEventListener("error", onReady);
        };

        const onReady = () => {
          cleanup();
          resolve();
        };

        video.addEventListener("loadeddata", onReady);
        video.addEventListener("canplay", onReady);
        video.addEventListener("error", onReady);

        if (video.readyState === 0) video.load();
      });
    });

    await Promise.all(loadPromises);
  }

  async startVideoSequence() {
    if (!this.videos.length) return;

    this.currentVideoIndex = 0;
    this.retryCount = 0;

    try {
      await this.waitForVideosToLoad();
      this.isPlaying = true;
      await this.playVideo(0);
    } catch (error) {
      console.error("BTS fans video sequence failed:", error);
      this.isPlaying = false;
      this.hasStarted = false;
    }
  }

  async playVideo(index) {
    if (index >= this.videos.length) {
      this.isPlaying = false;
      return;
    }

    const video = this.videos[index];
    if (!video) return;

    this.videos.forEach((v, i) => {
      if (i === index) return;
      v.pause();
      v.classList.remove("playing");
    });

    try {
      video.currentTime = 0;
      await video.play();
      video.classList.add("playing");
    } catch (error) {
      console.error(`BTS fans video ${index + 1} play failed:`, error);
      this.onVideoError(index);
    }
  }

  advanceFrom(index) {
    this.retryCount = 0;
    this.currentVideoIndex =
      index === this.videos.length - 1 ? 0 : index + 1;
    this.playVideo(this.currentVideoIndex);
  }

  onVideoEnded(index) {
    if (this.videos[index]) {
      this.videos[index].classList.remove("playing");
    }
    this.advanceFrom(index);
  }

  onVideoError(index) {
    if (this.videos[index]) {
      this.videos[index].classList.remove("playing");
    }

    this.retryCount += 1;
    if (this.retryCount >= this.maxRetries) {
      this.isPlaying = false;
      return;
    }

    this.currentVideoIndex =
      index === this.videos.length - 1 ? 0 : index + 1;
    this.playVideo(this.currentVideoIndex);
  }
}

let bts26FansVideoManager = null;

function initBts26FansVideos() {
  if (!bts26FansVideoManager) {
    bts26FansVideoManager = new Bts26FansVideoManager();
  }
}

function initBts26() {
  initBts26CartManager();
  initBts26MiniCarousels();
  initBts26ComboTierSync();
  initBts26FansMarquee();
  initBts26FansVideos();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initBts26);
} else {
  initBts26();
}

